import { Box, Button, Grid, Typography } from "@mui/material";
import { useState, type FC } from "react";
import { PayModal } from "../modals/PayModal";
import {
  PayAPIRequestBodyType,
  getFetch,
  postFetch,
} from "../../utils/http.util";
import {
  API_PAYMENT_OPEN,
  API_PAYMENT_STATE,
  ERR_MSG_FAIL_GET_PAYMENT_STATUS,
  ERR_MSG_FAIL_MONTH_PAY,
  ERR_MSG_FAIL_YEAR_PAY,
  ERR_MSG_GET_PAYMENT_STATUS,
  ERR_MSG_MONTH_PAY,
  ERR_MSG_NOT_LOGIN,
  ERR_MSG_UNKNOWN,
  ERR_MSG_UNKNOWN_PAYMENT,
  ERR_MSG_UNKNOWN_PAYMENT_STATUS,
  ERR_MSG_YEAR_PAY,
  MONTH_PAY_CENT,
  PAY_STATE_ENUM,
  PAY_STATE_MAP,
  PAY_TYPE_ENUM,
  PAY_TYPE_MAP,
  YEAR_PAY_CENT,
  API_ERR_CODE_ENUM,
  API_ERR_CODE_DISPLAY_ENUM,
} from "../../utils/constants";

type DownRightsPayBlockProps = {
  onPaySuccess: () => void;
  showPromptMsg: (msg: string) => void;
};

// https://pay.weixin.qq.com/doc/v3/merchant/4012791874
export const DownRightsPayBlock: FC<DownRightsPayBlockProps> = ({
  onPaySuccess,
  showPromptMsg,
}) => {
  const [payType, setPayType] = useState<PAY_TYPE_ENUM | null>(null);
  const [showPayModal, setShowPayModal] = useState(false);
  const [qrCodeURL, setQrCodeURL] = useState("");
  const [tradeID, setTradeID] = useState("");

  /* ------------------------------ 一月 99¥ ------------------------------------------------------------ */
  const handleMonthPayAPIFetchData = (data: any) => {
    console.log("-> handleMonthPayAPIFetchData, data:", data);
    /**
     * url: string, 用来生产二维码 - weixin://wxpay/bizpayurl?pr=wWIR5MNz3
     * tradeid: string, 查订单状态要用 - fd74b51cf3dbeea94506286ff2aa7066
     */
    if (data?.url) {
      setShowPayModal(true);
      setQrCodeURL(data.url ?? "");
      setTradeID(data.tradeid ?? "");
    } else if (data?.errCode === API_ERR_CODE_ENUM.unauthorized) {
      showPromptMsg(API_ERR_CODE_DISPLAY_ENUM.unauthorized);
    } else {
      showPromptMsg(
        `${ERR_MSG_FAIL_MONTH_PAY} ${
          data?.code || data?.errCode || ERR_MSG_UNKNOWN
        }`
      );
    }
  };

  const handleMonthPayAPIFetchErr = (err: any) => {
    console.log("-> handleMonthPayAPIFetchErr, err:", err);
    showPromptMsg(ERR_MSG_MONTH_PAY);
  };

  const handleMonthPayAPIWithoutAuth = () => {
    showPromptMsg(ERR_MSG_NOT_LOGIN);
  };

  // 一月 99¥
  const handleMonthPayClick = () => {
    setPayType(PAY_TYPE_ENUM.monthly);

    const requestBody: PayAPIRequestBodyType = { amount: MONTH_PAY_CENT };
    postFetch(
      API_PAYMENT_OPEN,
      requestBody,
      handleMonthPayAPIFetchData,
      handleMonthPayAPIFetchErr,
      handleMonthPayAPIWithoutAuth
    );
  };

  /* ------------------------------ 一年 299¥ ------------------------------------------------------------ */
  const handleYearPayAPIFetchData = (data: any) => {
    console.log("-> handleYearPayAPIFetchData, data:", data);
    /**
     * url: string, 用来生产二维码
     * tradeid: string, 查订单状态要用
     */
    if (data?.url) {
      setShowPayModal(true);
      setQrCodeURL(data.url ?? "");
      setTradeID(data.tradeid ?? "");
    } else if (data?.errCode === API_ERR_CODE_ENUM.unauthorized) {
      showPromptMsg(API_ERR_CODE_DISPLAY_ENUM.unauthorized);
    } else {
      showPromptMsg(
        `${ERR_MSG_FAIL_YEAR_PAY} ${
          data?.code || data?.errCode || ERR_MSG_UNKNOWN
        }`
      );
    }
  };

  const handleYearPayAPIFetchErr = (err: any) => {
    console.log("-> handleYearPayAPIFetchErr, err:", err);
    showPromptMsg(ERR_MSG_YEAR_PAY);
  };

  const handleYearPayAPIWithoutAuth = () => {
    showPromptMsg(ERR_MSG_NOT_LOGIN);
  };

  // 一年 299¥
  const handleYearPayClick = () => {
    setPayType(PAY_TYPE_ENUM.yearly);

    const requestBody: PayAPIRequestBodyType = { amount: YEAR_PAY_CENT };
    postFetch(
      API_PAYMENT_OPEN,
      requestBody,
      handleYearPayAPIFetchData,
      handleYearPayAPIFetchErr,
      handleYearPayAPIWithoutAuth
    );
  };

  /* ------------------------------ 查询支付订单 ------------------------------------------------------------ */
  const handlePayedAPIFetchData = (data: any) => {
    console.log("-> handlePayedAPIFetchData, data:", data);

    const prefixStr =
      payType === PAY_TYPE_ENUM.monthly
        ? PAY_TYPE_MAP.monthly
        : payType === PAY_TYPE_ENUM.yearly
        ? PAY_TYPE_MAP.yearly
        : ERR_MSG_UNKNOWN_PAYMENT;

    if (data?.state) {
      switch (data.state) {
        case PAY_STATE_ENUM.onGoing: {
          showPromptMsg(`${prefixStr} ${PAY_STATE_MAP.onGoing}`);
          break;
        }

        case PAY_STATE_ENUM.success: {
          showPromptMsg(`${prefixStr} ${PAY_STATE_MAP.success}`);
          onPaySuccess();
          setShowPayModal(false);
          break;
        }

        case PAY_STATE_ENUM.failed: {
          showPromptMsg(`${prefixStr} ${PAY_STATE_MAP.failed}`);
          break;
        }

        case PAY_STATE_ENUM.refund: {
          showPromptMsg(`${prefixStr} ${PAY_STATE_MAP.refund}`);
          break;
        }

        default: {
          showPromptMsg(ERR_MSG_UNKNOWN_PAYMENT_STATUS);
          break;
        }
      }
    } else if (data?.errCode === API_ERR_CODE_ENUM.unauthorized) {
      showPromptMsg(API_ERR_CODE_DISPLAY_ENUM.unauthorized);
    } else {
      showPromptMsg(
        `${ERR_MSG_FAIL_GET_PAYMENT_STATUS} ${prefixStr} ${
          data?.code || data?.errCode || ERR_MSG_UNKNOWN
        }`
      );
    }
  };

  const handlePayedAPIFetchErr = (err: any) => {
    console.log("-> handlePayedAPIFetchErr, err:", err);

    const prefixStr =
      payType === PAY_TYPE_ENUM.monthly
        ? PAY_TYPE_MAP.monthly
        : payType === PAY_TYPE_ENUM.yearly
        ? PAY_TYPE_MAP.yearly
        : ERR_MSG_UNKNOWN_PAYMENT;

    showPromptMsg(`${ERR_MSG_GET_PAYMENT_STATUS} ${prefixStr}`);
  };

  const handlePayedAPIWithoutAuth = () => {
    showPromptMsg(ERR_MSG_NOT_LOGIN);
  };

  // 支付完成
  /**
   * 查订单状态：GET /api/payment/state
   * query：tradeid
   * response：
   * {
   *   tradeid: string,
   *   state: string, on going / success / failed / refund
   *   desc: string,
   * }
   */
  const handlePayedClick = () => {
    console.log("-> handlePayedClick, tradeID:", tradeID);

    getFetch(
      `${API_PAYMENT_STATE}?tradeid=${tradeID}`,
      handlePayedAPIFetchData,
      handlePayedAPIFetchErr,
      handlePayedAPIWithoutAuth
    );
  };

  return (
    <Box
      sx={{
        width: "100%",
        height: "50%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        gap: "16px",
        zIndex: 1,
      }}
    >
      <Grid container spacing={2}>
        <Grid
          size={12}
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "8px",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Typography
            variant="h5"
            fontWeight="bold"
          >{`会员权限介绍`}</Typography>
          <Typography
            variant="h6"
            fontWeight="bold"
          >{`游客：每天获得30魔法豆，魔法图带水印`}</Typography>
          <Typography
            variant="h6"
            fontWeight="bold"
          >{`会员：每天获得1000魔法豆，且魔法图不带水印`}</Typography>
        </Grid>

        <Grid
          size={6}
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "8px",
            justifyContent: "center",
            alignItems: "center",
            padding: "16px 0",
          }}
        >
          <Button
            variant="contained"
            onClick={handleMonthPayClick}
            size="large"
            sx={{
              fontWeight: 800,
              fontSize: "16px",
            }}
          >
            {`一月 99¥`}
          </Button>
        </Grid>

        <Grid
          size={6}
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "8px",
            justifyContent: "center",
            alignItems: "center",
            padding: "16px 0",
          }}
        >
          <Button
            variant="contained"
            onClick={handleYearPayClick}
            size="large"
            sx={{
              fontWeight: 800,
              fontSize: "16px",
            }}
          >
            {`一年 299¥`}
          </Button>
        </Grid>
      </Grid>

      <PayModal
        open={showPayModal}
        onCloseClick={() => setShowPayModal(false)}
        payType={payType}
        onPayedClick={() => {
          // TODO：支付完成，重新读取用户信息
          handlePayedClick();
        }}
        qrCodeUrl={qrCodeURL}
      />
    </Box>
  );
};
